// src/components/ui/3d-globe.jsx
import React, {
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Html, OrbitControls, useTexture } from "@react-three/drei";
import * as THREE from "three";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const DEFAULT_EARTH_TEXTURE =
  "https://unpkg.com/three-globe@2.31.0/example/img/earth-blue-marble.jpg";

const DEFAULT_BUMP_TEXTURE =
  "https://unpkg.com/three-globe@2.31.0/example/img/earth-topology.png";

function latLngToVector3(lat, lng, radius) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);

  const x = -(radius * Math.sin(phi) * Math.cos(theta));
  const z = radius * Math.sin(phi) * Math.sin(theta);
  const y = radius * Math.cos(phi);

  return new THREE.Vector3(x, y, z);
}

function Marker({ marker, radius }) {
  const [isVisible, setIsVisible] = useState(true);
  const visibleRef = useRef(true);
  const imageGroupRef = useRef(null);
  const { camera } = useThree();

  const surfacePosition = useMemo(() => {
    return latLngToVector3(marker.lat, marker.lng, radius * 1.01);
  }, [marker.lat, marker.lng, radius]);

  
  const lineTipPosition = useMemo(() => {
    return latLngToVector3(marker.lat, marker.lng, radius * 1.17);
  }, [marker.lat, marker.lng, radius]);

  
  const logoPosition = useMemo(() => {
    return latLngToVector3(marker.lat, marker.lng, radius * 1.17); 
  }, [marker.lat, marker.lng, radius]);

  const lineHeight = lineTipPosition.distanceTo(surfacePosition);

  useFrame(() => {
    if (!imageGroupRef.current) return;
    const worldPos = new THREE.Vector3();
    imageGroupRef.current.getWorldPosition(worldPos);
    const markerDirection = worldPos.clone().normalize();
    const cameraDirection = camera.position.clone().normalize();
    const dot = markerDirection.dot(cameraDirection);
    const nextVisible = dot > -0.05;
    if (visibleRef.current !== nextVisible) {
      visibleRef.current = nextVisible;
      setIsVisible(nextVisible);
    }
  });

  const { lineCenter, lineQuaternion } = useMemo(() => {
    const center = surfacePosition.clone().lerp(lineTipPosition, 0.5);
    const direction = lineTipPosition.clone().sub(surfacePosition).normalize();
    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
    return { lineCenter: center, lineQuaternion: quaternion };
  }, [surfacePosition, lineTipPosition]);

  return (
    <group visible={isVisible}>
      {/* Línea desde el globo hasta la punta */}
      <mesh position={lineCenter} quaternion={lineQuaternion}>
        <cylinderGeometry args={[0.004, 0.004, lineHeight, 8]} />
        <meshBasicMaterial color="#5d83f3" transparent opacity={0.75} />
      </mesh>

      {/* Punto de inicio en la superficie */}
      <mesh position={surfacePosition} quaternion={lineQuaternion}>
        <coneGeometry args={[0.018, 0.045, 8]} />
        <meshBasicMaterial color="#204b97" />
      </mesh>

      
      <group ref={imageGroupRef} position={logoPosition}>
        <Html
          transform
          center
          sprite
          distanceFactor={10}
          style={{
            pointerEvents: "none",
            opacity: isVisible ? 1 : 0,
            transition: "opacity 0.15s ease-out",
          }}
        >
          <div
            className="grid place-items-center overflow-hidden rounded-full border border-white/40 bg-white shadow-lg"
            style={{
              width: "15px",
              height: "15px",
              padding: "3px",
              
            }}
          >
            <img
              src={marker.src}
              alt={marker.label || "Zentra Group"}
              className="h-full w-full object-contain"
              draggable={false}
            />
          </div>
        </Html>
      </group>
    </group>
  );
}

function RotatingGlobe({ config, markers }) {
  const groupRef = useRef(null);

  const [earthTexture, bumpTexture] = useTexture([
    config.textureUrl,
    config.bumpMapUrl,
  ]);

  useMemo(() => {
    if (earthTexture) {
      earthTexture.colorSpace = THREE.SRGBColorSpace;
      earthTexture.anisotropy = 16;
    }

    if (bumpTexture) {
      bumpTexture.anisotropy = 8;
    }
  }, [earthTexture, bumpTexture]);

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    if (!config.autoRotateSpeed) return;

    groupRef.current.rotation.y += delta * config.autoRotateSpeed * 0.35;
  });

  const geometry = useMemo(() => {
    return new THREE.SphereGeometry(config.radius, 64, 64);
  }, [config.radius]);

  return (
    <group ref={groupRef}>
      <mesh geometry={geometry}>
        <meshStandardMaterial
          map={earthTexture}
          bumpMap={bumpTexture}
          bumpScale={config.bumpScale * 0.05}
          roughness={0.72}
          metalness={0}
        />
      </mesh>

      {config.showWireframe && (
        <mesh>
          <sphereGeometry args={[config.radius * 1.003, 32, 16]} />
          <meshBasicMaterial
            color={config.wireframeColor}
            wireframe
            transparent
            opacity={0.08}
          />
        </mesh>
      )}

      {markers.map((marker, index) => (
        <Marker
          key={`marker-${index}-${marker.lat}-${marker.lng}`}
          marker={marker}
          radius={config.radius}
        />
      ))}
    </group>
  );
}

function Atmosphere({ radius, color, intensity, blur }) {
  const fresnelPower = Math.max(0.5, 5 - blur);

  const atmosphereMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        atmosphereColor: { value: new THREE.Color(color) },
        intensity: { value: intensity },
        fresnelPower: { value: fresnelPower },
      },
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vPosition;

        void main() {
          vNormal = normalize(normalMatrix * normal);
          vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 atmosphereColor;
        uniform float intensity;
        uniform float fresnelPower;

        varying vec3 vNormal;
        varying vec3 vPosition;

        void main() {
          float fresnel = pow(1.0 - abs(dot(vNormal, normalize(-vPosition))), fresnelPower);
          gl_FragColor = vec4(atmosphereColor, fresnel * intensity);
        }
      `,
      side: THREE.BackSide,
      transparent: true,
      depthWrite: false,
    });
  }, [color, intensity, fresnelPower]);

  return (
    <mesh scale={[1.13, 1.13, 1.13]}>
      <sphereGeometry args={[radius, 64, 32]} />
      <primitive object={atmosphereMaterial} attach="material" />
    </mesh>
  );
}

function Scene({ markers, config }) {
  const { camera } = useThree();

  useEffect(() => {
    camera.position.set(0, 0, config.radius * 3.5);
    camera.lookAt(0, 0, 0);
  }, [camera, config.radius]);

  return (
    <>
      <ambientLight intensity={config.ambientIntensity} />

      <directionalLight
        position={[config.radius * 5, config.radius * 2, config.radius * 5]}
        intensity={config.pointLightIntensity}
        color="#ffffff"
      />

      <directionalLight
        position={[-config.radius * 3, config.radius, -config.radius * 2]}
        intensity={config.pointLightIntensity * 0.35}
        color="#5d83f3"
      />

      <RotatingGlobe config={config} markers={markers} />

      {config.showAtmosphere && (
        <Atmosphere
          radius={config.radius}
          color={config.atmosphereColor}
          intensity={config.atmosphereIntensity}
          blur={config.atmosphereBlur}
        />
      )}

      <OrbitControls
        makeDefault
        enabled={false}
        enablePan={false}
        enableZoom={false}
        enableRotate={false}
      />
    </>
  );
}

function LoadingFallback() {
  return (
    <Html center>
      <div className="text-sm text-white/60">Cargando globo...</div>
    </Html>
  );
}

const defaultConfig = {
  radius: 2,
  globeColor: "#0f172a",
  textureUrl: DEFAULT_EARTH_TEXTURE,
  bumpMapUrl: DEFAULT_BUMP_TEXTURE,
  showAtmosphere: true,
  atmosphereColor: "#5d83f3",
  atmosphereIntensity: 0.55,
  atmosphereBlur: 2,
  bumpScale: 1,
  autoRotateSpeed: 0.35,
  enableZoom: false,
  enablePan: false,
  minDistance: 5,
  maxDistance: 15,
  markerSize: 0.06,
  showWireframe: false,
  wireframeColor: "#5d83f3",
  ambientIntensity: 0.65,
  pointLightIntensity: 1.55,
  backgroundColor: null,
};

export function Globe3D({ markers = [], config = {}, className = "" }) {
  const mergedConfig = useMemo(() => {
    return { ...defaultConfig, ...config };
  }, [config]);

  return (
    <div className={cn("relative h-[500px] w-full", className)}>
      <Canvas
        gl={{
          antialias: true,
          alpha: true,
          powerPreference: "high-performance",
        }}
        dpr={[1, 2]}
        camera={{
          fov: 45,
          near: 0.1,
          far: 1000,
          position: [0, 0, mergedConfig.radius * 3.5],
        }}
        style={{
          background: mergedConfig.backgroundColor || "transparent",
          pointerEvents: "none",
        }}
      >
        <Suspense fallback={<LoadingFallback />}>
          <Scene markers={markers} config={mergedConfig} />
        </Suspense>
      </Canvas>
    </div>
  );
}

export default Globe3D;